import React, { useEffect } from 'react';
import { FiBell, FiX } from 'react-icons/fi';
import useNotificationStore from '@/stores/NotificationStore';
import './Notifications.css';

const Notifications: React.FC = () => {
  const {
    notifications,
    loading,
    fetchNotifications,
    markNotificationAsRead,
    deleteNotification
  } = useNotificationStore();

  useEffect(() => {
    fetchNotifications(100, 0); // Fetch all notifications
  }, [fetchNotifications]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'marketing':
        return '📢';
      case 'system':
        return '⚙️';
      case 'announcement':
        return '📣';
      case 'instructor':
        return '👨‍🏫';
      case 'event':
        return '📅';
      case 'course':
        return '📚';
      default:
        return '🔔';
    }
  };

  const handleNotificationClick = (notification: any) => {
    if (!notification.is_read) {
      markNotificationAsRead(notification.uuid);
    }

    if (notification.action_url) {
      window.location.href = notification.action_url;
    }
  };

  const handleDelete = (e: React.MouseEvent, uuid: string) => {
    e.stopPropagation();
    deleteNotification(uuid);
  };

  // Group notifications by time period
  const groupNotificationsByDate = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: {
      today: any[];
      yesterday: any[];
      older: { [key: string]: any[] };
    } = {
      today: [],
      yesterday: [],
      older: {}
    };

    notifications.forEach((notification) => {
      const notifDate = new Date(notification.created_at);
      const notifDay = new Date(notifDate.getFullYear(), notifDate.getMonth(), notifDate.getDate());

      if (notifDay.getTime() === today.getTime()) {
        groups.today.push(notification);
      } else if (notifDay.getTime() === yesterday.getTime()) {
        groups.yesterday.push(notification);
      } else {
        const dateKey = notifDate.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
        if (!groups.older[dateKey]) {
          groups.older[dateKey] = [];
        }
        groups.older[dateKey].push(notification);
      }
    });

    return groups;
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const groupedNotifications = groupNotificationsByDate();

  return (
    <div className="notifications-page">
      <div className="notifications-page-header">
        <div className="notifications-page-title-section">
          <FiBell size={28} className="notifications-page-icon" />
          <div>
            <h1 className="notifications-page-title">Notifications</h1>
            <p className="notifications-page-subtitle">Stay updated with your latest notifications</p>
          </div>
        </div>
      </div>

      <div className="notifications-page-content">
        {loading ? (
          <div className="notifications-page-loading">
            <div className="notifications-loading-spinner"></div>
            <p>Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notifications-page-empty">
            <FiBell size={64} />
            <h3>No Notifications</h3>
            <p>You're all caught up! Check back later for new updates.</p>
          </div>
        ) : (
          <div className="notifications-groups">
            {/* Today's Notifications */}
            {groupedNotifications.today.length > 0 && (
              <div className="notifications-group">
                <h2 className="notifications-group-title">Today</h2>
                <div className="notifications-group-list">
                  {groupedNotifications.today.map((notification) => (
                    <div
                      key={notification.uuid}
                      className={`notification-card ${!notification.is_read ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="notification-card-icon">
                        {getNotificationIcon(notification.notification_type)}
                      </div>
                      <div className="notification-card-content">
                        <div className="notification-card-header">
                          <h4 className="notification-card-title">{notification.title}</h4>
                          <span className="notification-card-time">{formatTime(notification.created_at)}</span>
                        </div>
                        <p className="notification-card-message">{notification.message}</p>
                      </div>
                      {!notification.is_read && <div className="notification-unread-dot"></div>}
                      <button
                        className="notification-delete-btn"
                        onClick={(e) => handleDelete(e, notification.uuid)}
                        title="Delete notification"
                      >
                        <FiX size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Yesterday's Notifications */}
            {groupedNotifications.yesterday.length > 0 && (
              <div className="notifications-group">
                <h2 className="notifications-group-title">Yesterday</h2>
                <div className="notifications-group-list">
                  {groupedNotifications.yesterday.map((notification) => (
                    <div
                      key={notification.uuid}
                      className={`notification-card ${!notification.is_read ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="notification-card-icon">
                        {getNotificationIcon(notification.notification_type)}
                      </div>
                      <div className="notification-card-content">
                        <div className="notification-card-header">
                          <h4 className="notification-card-title">{notification.title}</h4>
                          <span className="notification-card-time">{formatTime(notification.created_at)}</span>
                        </div>
                        <p className="notification-card-message">{notification.message}</p>
                      </div>
                      {!notification.is_read && <div className="notification-unread-dot"></div>}
                      <button
                        className="notification-delete-btn"
                        onClick={(e) => handleDelete(e, notification.uuid)}
                        title="Delete notification"
                      >
                        <FiX size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Older Notifications (grouped by date) */}
            {Object.keys(groupedNotifications.older).length > 0 &&
              Object.entries(groupedNotifications.older)
                .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())
                .map(([dateKey, notifs]) => (
                  <div key={dateKey} className="notifications-group">
                    <h2 className="notifications-group-title">{dateKey}</h2>
                    <div className="notifications-group-list">
                      {notifs.map((notification) => (
                        <div
                          key={notification.uuid}
                          className={`notification-card ${!notification.is_read ? 'unread' : ''}`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="notification-card-icon">
                            {getNotificationIcon(notification.notification_type)}
                          </div>
                          <div className="notification-card-content">
                            <div className="notification-card-header">
                              <h4 className="notification-card-title">{notification.title}</h4>
                              <span className="notification-card-time">{formatTime(notification.created_at)}</span>
                            </div>
                            <p className="notification-card-message">{notification.message}</p>
                          </div>
                          {!notification.is_read && <div className="notification-unread-dot"></div>}
                          <button
                            className="notification-delete-btn"
                            onClick={(e) => handleDelete(e, notification.uuid)}
                            title="Delete notification"
                          >
                            <FiX size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
